import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Modal,
  Card,
  Button,
  Typography,
  Tag,
  Divider,
  Alert,
  Spin,
  Space,
  Input,
  App,
} from 'antd';
import {
  CheckCircleOutlined,
  CoffeeOutlined,
  PlusOutlined,
  MinusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useGetActiveDailyMenusQuery, useGetAnchorProductIdQuery } from '../../../services/dailyMenuApi';
import { DailyMenu, DailyMenuCourse, DailyMenuOption, DailyMenuCourseSelection } from '../../../types/dailyMenu';
import { ProductOrderItem } from '../../../types/products';

const { TextArea } = Input;

const { Title, Text } = Typography;

interface DailyMenuSelectionModalProps {
  visible: boolean;
  onCancel: () => void;
  onAddItem: (item: ProductOrderItem) => void;
  initialValues?: {
    dailyMenuId: string;
    quantity: number;
    notes?: string;
    dailyMenuSelections: DailyMenuCourseSelection[];
  };
  // courseIds whose selections were already comandeed to the kitchen. These render as
  // read-only (check, no +/-) and are excluded from handleConfirm output.
  sentCourseIds?: string[];
}

// Distribution: for each course, how many of each option
// { [courseId]: { [optionId]: quantity } }
type CourseDistribution = Record<string, Record<string, number>>;

// Trim each course's distribution so it sums to at most newTotal. Removes options from the
// last-inserted key first; relies on ECMAScript object string-key insertion order.
const trimDistributionToTotal = (
  distribution: CourseDistribution,
  newTotal: number,
): CourseDistribution => {
  if (newTotal <= 0) return {};
  const trimmed: CourseDistribution = {};
  for (const courseId of Object.keys(distribution)) {
    const courseDist = distribution[courseId] || {};
    const sum = Object.values(courseDist).reduce((s, q) => s + q, 0);
    if (sum <= newTotal) {
      trimmed[courseId] = { ...courseDist };
      continue;
    }
    const next: Record<string, number> = {};
    for (const [optionId, qty] of Object.entries(courseDist)) {
      next[optionId] = qty;
    }
    let excess = sum - newTotal;
    const optionIds = Object.keys(next);
    for (let i = optionIds.length - 1; i >= 0 && excess > 0; i--) {
      const optionId = optionIds[i];
      const qty = next[optionId];
      const drop = Math.min(qty, excess);
      next[optionId] = qty - drop;
      excess -= drop;
      if (next[optionId] === 0) delete next[optionId];
    }
    trimmed[courseId] = next;
  }
  return trimmed;
};

const DailyMenuSelectionModal: React.FC<DailyMenuSelectionModalProps> = ({
  visible,
  onCancel,
  onAddItem,
  initialValues,
  sentCourseIds,
}) => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const sentCourseIdSet = useMemo(() => new Set(sentCourseIds || []), [sentCourseIds]);
  const isAddingCoursesMode = sentCourseIdSet.size > 0;
  const [selectedMenu, setSelectedMenu] = useState<DailyMenu | null>(null);
  const [totalQuantity, setTotalQuantity] = useState(1);
  const [distribution, setDistribution] = useState<CourseDistribution>({});
  const [isMobile, setIsMobile] = useState(false);
  const [notes, setNotes] = useState('');
  const hasInitialized = useRef(false);
  const isEditing = !!initialValues;

  const { data: activeMenus = [], isLoading: menusLoading } = useGetActiveDailyMenusQuery(undefined, {
    skip: !visible,
  });

  const { data: anchorData } = useGetAnchorProductIdQuery(undefined, {
    skip: !visible,
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset state when modal opens (non-editing mode) or closes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!visible) {
      hasInitialized.current = false;
    } else if (!initialValues) {
      setSelectedMenu(null);
      setTotalQuantity(1);
      setDistribution({});
      setNotes('');
      hasInitialized.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Initialize from initialValues when editing (wait for activeMenus to load)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialValues && !hasInitialized.current && activeMenus.length > 0) {
      const menu = activeMenus.find(m => m.id === initialValues.dailyMenuId);
      if (menu) {
        hasInitialized.current = true;
        setSelectedMenu(menu);
        setTotalQuantity(initialValues.quantity);
        // Reconstruct distribution from dailyMenuSelections
        // Fixed courses don't have optionId, so they're filtered out naturally
        const reconstructed: CourseDistribution = {};
        for (const sel of initialValues.dailyMenuSelections) {
          if (sel.optionId) {
            reconstructed[sel.courseId] = {
              ...(reconstructed[sel.courseId] || {}),
              [sel.optionId]: (reconstructed[sel.courseId]?.[sel.optionId] || 0) + 1,
            };
          }
        }
        setDistribution(reconstructed);
        setNotes(initialValues.notes || '');
      } else {
        message.warning('Este menú ya no está disponible');
        onCancel();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenus, initialValues]);

  // Auto-select if single menu (only in non-editing mode)
  useEffect(() => {
    if (activeMenus.length === 1 && !selectedMenu && !initialValues) {
      setSelectedMenu(activeMenus[0]);
    }
  }, [activeMenus, selectedMenu, initialValues]);

  const selectableCourses = useMemo(() => {
    if (!selectedMenu) return [];
    return selectedMenu.courses
      .filter((c) => c.isSelectable)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [selectedMenu]);

  const fixedCourses = useMemo(() => {
    if (!selectedMenu) return [];
    return selectedMenu.courses
      .filter((c) => !c.isSelectable)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [selectedMenu]);

  // Get the sum of a course's distribution
  const getCourseSum = useCallback(
    (courseId: string) => {
      const courseDist = distribution[courseId] || {};
      return Object.values(courseDist).reduce((s, q) => s + q, 0);
    },
    [distribution]
  );

  // Check if a course is complete (sum === totalQuantity)
  const isCourseComplete = useCallback(
    (courseId: string) => getCourseSum(courseId) === totalQuantity,
    [getCourseSum, totalQuantity]
  );

  // Check if a course is empty (no options selected)
  const isCourseEmpty = useCallback(
    (courseId: string) => getCourseSum(courseId) === 0,
    [getCourseSum]
  );

  // A menu built only from fixed courses has nothing to distribute: every diner already gets
  // the whole menu. It needs its own branch everywhere the logic below reasons about picked
  // options, otherwise it reads as "nothing selected" and the modal refuses to add it.
  const isFixedOnlyMenu = !!selectedMenu && selectableCourses.length === 0;

  // Partial-comandear allowed: at least one option is selected on any non-sent course. Each
  // diner's OrderItem gets only the courses it actually has an assignment for; diners that
  // are still undecided on a course just won't have it on their OrderItem and can fill it
  // later via "Agregar tiempos faltantes".
  const partialAllowed = useMemo(() => {
    if (isFixedOnlyMenu) return true;
    for (const courseId of Object.keys(distribution)) {
      if (sentCourseIdSet.has(courseId)) continue;
      const courseDist = distribution[courseId] || {};
      if (Object.values(courseDist).some((qty) => qty > 0)) return true;
    }
    return false;
  }, [distribution, sentCourseIdSet, isFixedOnlyMenu]);

  // How many menús the confirm will actually create: a diner only becomes an OrderItem if it
  // has at least one course assigned. Assignments fill diners from index 0 up, so the widest
  // course decides. Shown on the button so "Cantidad de menús: 7" with 3 options picked can
  // never quietly read as 7 menús on the ticket.
  const menusToCreate = useMemo(() => {
    if (isEditing) return 1;
    if (isFixedOnlyMenu) return totalQuantity;
    let widest = 0;
    for (const courseId of Object.keys(distribution)) {
      const sum = Object.values(distribution[courseId] || {}).reduce((s, q) => s + q, 0);
      if (sum > widest) widest = sum;
    }
    return Math.min(totalQuantity, widest);
  }, [distribution, totalQuantity, isEditing, isFixedOnlyMenu]);

  const hasUnsentIncomplete = useMemo(
    () => selectableCourses.some((c) =>
      !sentCourseIdSet.has(c.id) && !isCourseComplete(c.id) && !isCourseEmpty(c.id)
    ),
    [selectableCourses, sentCourseIdSet, isCourseComplete, isCourseEmpty]
  );

  // Calculate total price including modifiers
  const totalPrice = useMemo(() => {
    if (!selectedMenu) return 0;
    let modifierTotal = 0;
    for (const courseId of Object.keys(distribution)) {
      const course = selectedMenu.courses.find((c) => c.id === courseId);
      if (!course) continue;
      for (const [optionId, qty] of Object.entries(distribution[courseId])) {
        const option = course.options.find((o) => o.id === optionId);
        if (option && option.priceModifier > 0) {
          modifierTotal += option.priceModifier * qty;
        }
      }
    }
    return totalQuantity * selectedMenu.price + modifierTotal;
  }, [selectedMenu, totalQuantity, distribution]);

  const handleOptionQuantityChange = (courseId: string, optionId: string, delta: number) => {
    setDistribution((prev) => {
      const courseDist = { ...(prev[courseId] || {}) };
      const current = courseDist[optionId] || 0;
      const newVal = Math.max(0, current + delta);

      // Don't allow exceeding totalQuantity for this course
      const otherSum = Object.entries(courseDist)
        .filter(([id]) => id !== optionId)
        .reduce((s, [, q]) => s + q, 0);

      const capped = Math.min(newVal, totalQuantity - otherSum);
      courseDist[optionId] = capped;

      // Remove zero entries
      if (courseDist[optionId] === 0) {
        delete courseDist[optionId];
      }

      return { ...prev, [courseId]: courseDist };
    });
  };

  // Quick-fill: if only one option available, auto-fill it
  const handleAutoFillCourse = (course: DailyMenuCourse) => {
    const available = course.options.filter((o) => o.isAvailable);
    if (available.length === 1) {
      setDistribution((prev) => ({
        ...prev,
        [course.id]: { [available[0].id]: totalQuantity },
      }));
    }
  };

  // Generate N individual ProductOrderItem from the distribution
  const handleConfirm = () => {
    if (!selectedMenu || !anchorData?.productId) return;

    // Fixed course selections (same for all)
    const fixedSelections: DailyMenuCourseSelection[] = fixedCourses.map((c) => ({
      courseId: c.id,
      courseName: c.name,
      optionName: c.fixedDescription || c.name,
      priceModifier: 0,
    }));

    const sortSelections = (selections: DailyMenuCourseSelection[]) => {
      selections.sort((a, b) => {
        const courseA = selectedMenu.courses.find((c) => c.id === a.courseId);
        const courseB = selectedMenu.courses.find((c) => c.id === b.courseId);
        return (courseA?.sortOrder ?? 0) - (courseB?.sortOrder ?? 0);
      });
    };

    // Editing mode: generate only 1 item. In add-courses mode (post-comandear), skip the
    // courses already sent — only the newly-added courses are emitted. Edit mode is always
    // single-diner so quantity is 1 per option (no half-filled scenario here).
    if (isEditing) {
      const selections: DailyMenuCourseSelection[] = [...fixedSelections];
      let modifierSum = 0;
      for (const [courseId, optionDist] of Object.entries(distribution)) {
        if (sentCourseIdSet.has(courseId)) continue;
        const course = selectedMenu.courses.find(c => c.id === courseId);
        for (const [optionId, qty] of Object.entries(optionDist)) {
          if (qty > 0) {
            const option = course?.options.find(o => o.id === optionId);
            if (option) {
              selections.push({
                courseId,
                courseName: course!.name,
                optionId,
                optionName: option.name,
                priceModifier: option.priceModifier,
              });
              modifierSum += option.priceModifier;
            }
          }
        }
      }
      sortSelections(selections);
      const item: ProductOrderItem = {
        productId: anchorData.productId,
        productName: selectedMenu.name,
        quantity: 1,
        selectedVariants: [],
        unitPrice: selectedMenu.price + modifierSum,
        dailyMenuId: selectedMenu.id,
        dailyMenuName: selectedMenu.name,
        dailyMenuSelections: selections,
        notes: notes.trim() || undefined,
      };
      onAddItem(item);
      onCancel();
      return;
    }

    // Normal mode: build assignments per course (may be partial — fewer than totalQuantity),
    // then zip across diners. Diners that don't have an assignment for a given course just
    // skip it on their OrderItem; they can add it later via "Agregar tiempos faltantes".
    type OptionAssignment = { courseId: string; courseName: string; optionId: string; optionName: string; priceModifier: number; sortOrder: number };
    const courseAssignments: OptionAssignment[][] = [];

    for (const course of selectableCourses) {
      const courseDist = distribution[course.id] || {};
      const assignments: OptionAssignment[] = [];
      for (const [optionId, qty] of Object.entries(courseDist)) {
        const option = course.options.find((o) => o.id === optionId);
        if (!option) continue;
        for (let i = 0; i < qty; i++) {
          assignments.push({
            courseId: course.id,
            courseName: course.name,
            optionId: option.id,
            optionName: option.name,
            priceModifier: option.priceModifier,
            sortOrder: course.sortOrder,
          });
        }
      }
      if (assignments.length > 0) courseAssignments.push(assignments);
    }

    for (let i = 0; i < totalQuantity; i++) {
      const selections: DailyMenuCourseSelection[] = [...fixedSelections];
      let modifierSum = 0;
      let assignedCourses = 0;

      for (const assignments of courseAssignments) {
        const a = assignments[i];
        if (!a) continue; // diner i has no choice yet for this course — fill in later
        selections.push({
          courseId: a.courseId,
          courseName: a.courseName,
          optionId: a.optionId,
          optionName: a.optionName,
          priceModifier: a.priceModifier,
        });
        modifierSum += a.priceModifier;
        assignedCourses++;
      }

      // A diner with no course at all is not a partial menu, it's an empty one: it prices at
      // $0, the kitchen gets a bare line, and it then blocks the payment with no way to
      // complete it (the incremental-courses endpoint rejects items that were never
      // comandeed). Skip it — the menú can simply be captured later.
      // Only when there is something to pick, though: on a fixed-only menu `selections`
      // already holds the whole menu and assignedCourses is legitimately 0.
      if (selectableCourses.length > 0 && assignedCourses === 0) continue;

      sortSelections(selections);

      const item: ProductOrderItem = {
        productId: anchorData.productId,
        productName: selectedMenu.name,
        quantity: 1,
        selectedVariants: [],
        unitPrice: selectedMenu.price + modifierSum,
        dailyMenuId: selectedMenu.id,
        dailyMenuName: selectedMenu.name,
        dailyMenuSelections: selections,
        notes: notes.trim() || undefined,
      };

      onAddItem(item);
    }

    onCancel();
  };

  // ================ Render helpers ================

  const renderMenuSelection = () => (
    <div style={{ padding: 16 }}>
      <Title level={5} style={{ marginBottom: 16 }}>
        Selecciona un menu
      </Title>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {activeMenus.map((menu) => (
          <Card
            key={menu.id}
            hoverable
            onClick={() => {
              setSelectedMenu(menu);
              setTotalQuantity(1);
              setDistribution({});
            }}
            style={{
              border: selectedMenu?.id === menu.id ? '2px solid #1890ff' : '1px solid var(--border-color-strong)',
              cursor: 'pointer',
            }}
            styles={{ body: { padding: 16 } }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong style={{ fontSize: 16 }}>{menu.name}</Text>
                {menu.description && (
                  <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>{menu.description}</Text>
                )}
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {menu.courses.filter((c) => c.isSelectable).length} tiempo
                  {menu.courses.filter((c) => c.isSelectable).length !== 1 ? 's' : ''}
                </Text>
              </div>
              <Tag color="green" style={{ fontSize: 16, padding: '4px 12px' }}>
                ${menu.price.toFixed(2)}
              </Tag>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderQuantitySelector = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '12px 16px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: 8,
        marginBottom: 16,
      }}
    >
      <Text strong>Cantidad de menus:</Text>
      <Button
        shape="circle"
        icon={<MinusOutlined />}
        onClick={() => {
          setTotalQuantity((q) => {
            const next = Math.max(1, q - 1);
            setDistribution((prev) => trimDistributionToTotal(prev, next));
            return next;
          });
        }}
        disabled={totalQuantity <= 1}
        size="large"
      />
      <Text strong style={{ fontSize: 24, minWidth: 40, textAlign: 'center' }}>
        {totalQuantity}
      </Text>
      <Button
        shape="circle"
        icon={<PlusOutlined />}
        onClick={() => setTotalQuantity((q) => q + 1)}
        size="large"
        type="primary"
      />
    </div>
  );

  const renderOptionRow = (course: DailyMenuCourse, option: DailyMenuOption) => {
    const qty = distribution[course.id]?.[option.id] || 0;
    const courseSum = getCourseSum(course.id);
    const canAdd = courseSum < totalQuantity;
    const isCourseSent = sentCourseIdSet.has(course.id);

    return (
      <div
        key={option.id}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: qty > 0
            ? (isCourseSent ? 'var(--bg-success-light)' : 'var(--bg-info-light)')
            : 'var(--bg-primary)',
          borderRadius: 6,
          border: qty > 0
            ? (isCourseSent ? '1px solid var(--border-success)' : '1px solid var(--border-info)')
            : '1px solid var(--border-color)',
          marginBottom: 6,
          transition: 'all 0.2s',
        }}
      >
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: 14 }}>{option.name}</Text>
          {option.priceModifier > 0 && (
            <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>
              +${option.priceModifier.toFixed(2)}
            </Tag>
          )}
          {option.description && (
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{option.description}</Text>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isCourseSent ? (
            <>
              <Text strong style={{ fontSize: 14, color: 'var(--color-success-text)' }}>
                {qty}
              </Text>
              {qty > 0 && (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} title="Ya enviado a cocina" />
              )}
            </>
          ) : (
            <>
              <Button
                size="small"
                shape="circle"
                icon={<MinusOutlined />}
                onClick={() => handleOptionQuantityChange(course.id, option.id, -1)}
                disabled={qty === 0}
              />
              <Text
                strong
                style={{
                  fontSize: 18,
                  minWidth: 28,
                  textAlign: 'center',
                  color: qty > 0 ? 'var(--color-info)' : 'var(--text-secondary)',
                }}
              >
                {qty}
              </Text>
              <Button
                size="small"
                shape="circle"
                icon={<PlusOutlined />}
                onClick={() => handleOptionQuantityChange(course.id, option.id, 1)}
                disabled={!canAdd || !option.isAvailable}
                type={canAdd && option.isAvailable ? 'primary' : 'default'}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCourseDistribution = (course: DailyMenuCourse) => {
    const courseSum = getCourseSum(course.id);
    const isComplete = courseSum === totalQuantity;
    const remaining = totalQuantity - courseSum;
    const availableOptions = course.options.filter((o) => o.isAvailable);
    const unavailableOptions = course.options.filter((o) => !o.isAvailable);
    const isCourseSent = sentCourseIdSet.has(course.id);

    return (
      <div key={course.id} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: 15 }}>{course.name}</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isCourseSent && availableOptions.length === 1 && !isComplete && (
              <Button
                size="small"
                type="link"
                onClick={() => handleAutoFillCourse(course)}
                style={{ padding: 0 }}
              >
                Todos
              </Button>
            )}
            {isCourseSent ? (
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
                Ya enviado
              </Tag>
            ) : (
              <Tag color={isComplete ? 'green' : 'orange'} style={{ margin: 0 }}>
                {courseSum}/{totalQuantity}
              </Tag>
            )}
          </div>
        </div>
        {availableOptions
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((option) => renderOptionRow(course, option))}
        {unavailableOptions
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((option) => (
            <div
              key={option.id}
              style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 6,
                border: '1px solid var(--border-color)',
                marginBottom: 6,
                opacity: 0.5,
              }}
            >
              <Text delete style={{ fontSize: 14 }}>{option.name}</Text>
              <Tag color="default" style={{ marginLeft: 8 }}>Agotado</Tag>
            </div>
          ))}
        {!isComplete && courseSum > 0 && (
          <Text type="warning" style={{ fontSize: 12 }}>
            <WarningOutlined /> Faltan {remaining} por asignar
          </Text>
        )}
      </div>
    );
  };

  const renderFixedCourses = () => {
    if (fixedCourses.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        {fixedCourses.map((course) => (
          <div
            key={course.id}
            style={{
              padding: '8px 12px',
              backgroundColor: 'var(--bg-success-light)',
              borderRadius: 6,
              marginBottom: 8,
              border: '1px solid var(--border-success)',
            }}
          >
            <Text strong style={{ fontSize: 13 }}>{course.name}:</Text>{' '}
            <Text style={{ fontSize: 13 }}>{course.fixedDescription || 'Incluido'}</Text>
          </div>
        ))}
      </div>
    );
  };

  const renderPriceSummary = () => (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: 'var(--bg-info-light)',
        borderRadius: 8,
        border: '1px solid var(--border-info)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div>
        <Text strong>{totalQuantity}× {selectedMenu?.name}</Text>
        {totalQuantity > 1 && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            ${selectedMenu?.price.toFixed(2)} c/u
          </Text>
        )}
      </div>
      <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
        ${totalPrice.toFixed(2)}
      </Title>
    </div>
  );

  // Main content
  const renderContent = () => {
    if (menusLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Cargando menus...</div>
        </div>
      );
    }

    if (activeMenus.length === 0) {
      return (
        <Alert
          type="info"
          message="No hay menus del dia activos"
          description="No se encontraron menus del dia disponibles para hoy."
          showIcon
          style={{ margin: 16 }}
        />
      );
    }

    // Step 1: Menu selection (if multiple and none selected)
    if (activeMenus.length > 1 && !selectedMenu) {
      return renderMenuSelection();
    }

    if (!selectedMenu) return null;

    return (
      <div style={{ padding: isMobile ? 12 : '16px 24px' }}>
        {/* Menu header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>{selectedMenu.name}</Title>
            {selectedMenu.description && (
              <Text type="secondary" style={{ fontSize: 13 }}>{selectedMenu.description}</Text>
            )}
          </div>
          {activeMenus.length > 1 && !isEditing && (
            <Button
              size="small"
              onClick={() => {
                setSelectedMenu(null);
                setDistribution({});
              }}
            >
              Cambiar
            </Button>
          )}
        </div>

        {/* Quantity selector (hidden in editing mode) */}
        {!isEditing && renderQuantitySelector()}

        {/* Partial-comandear hint */}
        {hasUnsentIncomplete && partialAllowed && !isAddingCoursesMode && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Los comensales sin opcion en algun tiempo no recibiran ese tiempo ahora; podras agregarselo despues desde la orden."
          />
        )}

        {/* The counter says N but only some diners have a choice — say so before confirming */}
        {!isEditing && !isAddingCoursesMode && menusToCreate > 0 && menusToCreate < totalQuantity && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Se agregaran ${menusToCreate} de ${totalQuantity} menus: los demas no tienen ningun tiempo asignado. Capturalos despues.`}
          />
        )}

        {/* Fixed courses */}
        {renderFixedCourses()}

        {/* Selectable courses with distribution */}
        {selectableCourses.map((course) => renderCourseDistribution(course))}

        <Divider style={{ margin: '12px 0' }} />

        {/* Price summary */}
        {renderPriceSummary()}

        {/* Notes */}
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
            Notas Especiales (Opcional)
          </Text>
          <TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ej: Sin cebolla, alergias, preferencias..."
            rows={2}
            maxLength={500}
            showCount
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <CoffeeOutlined />
          <span>{isEditing ? 'Editar Menu del Dia' : 'Menu del Dia'}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel}>Cancelar</Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedMenu || !partialAllowed || !anchorData?.productId}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            icon={<CheckCircleOutlined />}
          >
            {isAddingCoursesMode
              ? 'Enviar tiempos a cocina'
              : isEditing
                ? 'Guardar Cambios'
                : `Agregar ${menusToCreate} menu${menusToCreate !== 1 ? 's' : ''}`} ${totalPrice.toFixed(2)}
          </Button>
        </div>
      }
      width={isMobile ? '100%' : 600}
      style={{ top: isMobile ? 0 : 20 }}
      styles={{ body: { padding: 0, maxHeight: isMobile ? '75vh' : '65vh', overflow: 'auto' } }}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default DailyMenuSelectionModal;
